import type { SvgIconComponent } from "@mui/icons-material";
import DevicesIcon from "@mui/icons-material/DevicesRounded";
import FolderCopyIcon from "@mui/icons-material/FolderCopyOutlined";
import FolderSharedIcon from "@mui/icons-material/FolderSharedOutlined";
import InsightsIcon from "@mui/icons-material/InsightsRounded";
import LockPersonIcon from "@mui/icons-material/LockPersonOutlined";
import ManageAccountsIcon from "@mui/icons-material/ManageAccountsOutlined";
import PaletteIcon from "@mui/icons-material/PaletteOutlined";
import QueryStatsIcon from "@mui/icons-material/QueryStatsRounded";
import PromoImageAccess from "../../../../images/portal_promo_access.webp";
import PromoImageActivity from "../../../../images/portal_promo_activity.webp";
import PromoImageBranding from "../../../../images/portal_promo_branding.webp";
import PromoImageDashboard from "../../../../images/portal_promo_dashboard.webp";
import PromoImageDataroom from "../../../../images/portal_promo_dataroom.webp";
import PromoImageDocuments from "../../../../images/portal_promo_documents.webp";
import PromoImageMobile from "../../../../images/portal_promo_mobile.webp";
import PromoImageSelfService from "../../../../images/portal_promo_selfservice.webp";

export type CapabilityKey =
  "documents" | "dashboard" | "dataroom" | "access" | "selfservice" | "branding" | "activity" | "mobile";

export interface Capability {
  key: CapabilityKey;
  Icon: SvgIconComponent;
  title: string;
  subtitle: string;
  description: string;
  imageSrc: string;
  imageAspectRatio: string;
}

export const capabilities: Capability[] = [
  {
    key: "documents",
    Icon: FolderSharedIcon,
    title: "Secure Documents",
    subtitle: "Statements, notices & reports",
    description:
      "Publish every document to one secure, searchable inbox — organized by category, and delivered with email notifications the moment it goes live.",
    imageSrc: PromoImageDocuments,
    imageAspectRatio: "1920 / 984",
  },
  {
    key: "dashboard",
    Icon: InsightsIcon,
    title: "Investor Dashboard",
    subtitle: "Balance & cash flows",
    description:
      "Give investors their account balance, unfunded commitment, contributions, and cash flows at a glance — the answer to “how am I doing?” without a single email.",
    imageSrc: PromoImageDashboard,
    imageAspectRatio: "1920 / 1120",
  },
  {
    key: "dataroom",
    Icon: FolderCopyIcon,
    title: "Fundraising Data Room",
    subtitle: "Diligence materials",
    description:
      "Run your raise inside the portal: a permissioned data room for LPA, PPM, and diligence materials — with engagement metrics.",
    imageSrc: PromoImageDataroom,
    imageAspectRatio: "1920 / 1120",
  },
  {
    key: "access",
    Icon: LockPersonIcon,
    title: "Access Control",
    subtitle: "You decide who sees what",
    description:
      "Grant each contact access one document category at a time. Role-based permissions keep files with exactly the right people.",
    imageSrc: PromoImageAccess,
    imageAspectRatio: "1920 / 792",
  },
  {
    key: "selfservice",
    Icon: ManageAccountsIcon,
    title: "Investor Self-Service",
    subtitle: "Suggested updates, your approval",
    description:
      "Investors with the portal Admin role can propose changes to their own record — registered address, bank accounts — and every request routes to your team for review before anything changes.",
    imageSrc: PromoImageSelfService,
    imageAspectRatio: "1920 / 864",
  },
  {
    key: "branding",
    Icon: PaletteIcon,
    title: "Custom Branding",
    subtitle: "Your brand, your colors",
    description:
      "Set a custom logo, navigation color, accent — the portal, emails, and login all read as an extension of your firm.",
    imageSrc: PromoImageBranding,
    imageAspectRatio: "1920 / 1120",
  },
  {
    key: "activity",
    Icon: QueryStatsIcon,
    title: "Activity Tracking",
    subtitle: "Engagement insights",
    description:
      "See who viewed and downloaded each document, page by page, so you always know where investor attention is going.",
    imageSrc: PromoImageActivity,
    imageAspectRatio: "1668 / 1386",
  },
  {
    key: "mobile",
    Icon: DevicesIcon,
    title: "Any Device",
    subtitle: "Responsive by default",
    description:
      "A responsive experience from day one — investors get the same secure portal on desktop, tablet, and phone.",
    imageSrc: PromoImageMobile,
    imageAspectRatio: "1920 / 1120",
  },
];
